import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { apiClient } from "@/lib/api";
import { useLanguage } from "@/lib/LanguageContext";

// DB-powered homepage testimonials. Shows ONLY real published testimonials.
// Renders nothing unless at least 3 are published. Featured are prioritized; max 3.
export default function HomeTestimonials() {
    const { t } = useLanguage();
    const [items, setItems] = useState(null);

    useEffect(() => {
        apiClient
            .get("/testimonials/public")
            .then(({ data }) => {
                const list = Array.isArray(data) ? data : [];
                const sorted = [...list].sort((a, b) => (b.featured === a.featured ? 0 : b.featured ? 1 : -1));
                setItems(sorted.slice(0, 3));
            })
            .catch(() => setItems([]));
    }, []);

    if (!items || items.length < 3) return null;

    return (
        <section className="bg-pearl py-24 sm:py-32" data-testid="testimonials-section">
            <div className="container-luxe">
                <Reveal className="text-center max-w-3xl mx-auto mb-20">
                    <p className="overline text-gold">{t("home.trustedEyebrow")}</p>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("home.trustedTitle")}</h2>
                    <p className="mt-5 text-navy/70 leading-relaxed">{t("home.trustedBody")}</p>
                </Reveal>

                <div className="grid md:grid-cols-3 gap-10 lg:gap-14">
                    {items.map((tst, i) => (
                        <Reveal key={tst.id} delay={i * 100} className="text-center md:text-left" data-testid={`testimonial-${i}`}>
                            <Quote className="w-7 h-7 text-gold mx-auto md:mx-0 mb-6" strokeWidth={1.25} />
                            <p className="font-serif text-2xl leading-snug text-navy/90 mb-8 italic">&ldquo;{tst.public_text}&rdquo;</p>
                            <div className="flex items-center gap-3 justify-center md:justify-start">
                                {tst.photo && (
                                    <img src={tst.photo} alt={tst.public_display_name} className="w-11 h-11 rounded-full object-cover" data-testid={`testimonial-photo-${i}`} />
                                )}
                                <div>
                                    <p className="overline text-navy/55">{tst.public_display_name}</p>
                                    {(tst.community || tst.service_category) && (
                                        <p className="text-xs text-navy/45 mt-1 tracking-wide">{[tst.community, tst.service_category].filter(Boolean).join(" · ")}</p>
                                    )}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <div className="text-center mt-16">
                    <Link to="/testimonials" className="btn-luxe-outline inline-flex items-center gap-2" data-testid="home-testimonials-cta">
                        {t("home.trustedCta")} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
